// InvoicesListPage — раздел «Счета» (V14 W6, §3.8).
// Таблица (№ INV-YYYY-NNNN / Сделка / Дата / Сумма / Оплачено / Остаток / Срок / Статус / Действия),
// фильтры: lifecycle-статус (чипы DRAFT/OPEN/VOID), живой поиск q (по №), сделка — из URL ?dealId=
// (канон «Показать все →» в InvoiceBlock/PaymentsListPage). Пагинация 20 (канон платежей).
// Действия: DRAFT — «Выставить»/«Редактировать»/«Аннулировать»; OPEN — «Оплатить»/«Аннулировать».
// Создание — InvoiceNewDialog; «Оплатить» — PaymentNewDialog с presetInvoice (W7 §3.8).
// Валюта отображения — канон [RUB,USD,EUR,GBP] (совместимо с BE-каноном сделок).

import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, Box, Button, Chip, IconButton, Paper, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, TextField, Tooltip, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import CancelIcon from '@mui/icons-material/Cancel';
import { invoicesApi, type Invoice, type InvoiceLifecycleStatus } from '@/api/invoices';
import { PaymentNewDialog } from '@/pages/payments/components/PaymentNewDialog';
import { InvoiceNewDialog } from './components/InvoiceNewDialog';
import { InvoiceEditDialog } from './components/InvoiceEditDialog';
import { InvoiceStatusChip, invoiceStatusColor } from './components/InvoiceStatusChip';
import { formatDate, formatInvoiceNumber, formatInvoiceStatus, formatMoney } from '@/lib/format';
import { extractApiErrorMessage } from '@/lib/apiError';
import { debounce } from '@/utils/performance';
import { PageHeader } from '@/theme/PageHeader';
import { LoadingSpinner } from '@/theme/LoadingSpinner';
import { EmptyState } from '@/theme/EmptyState';
import { t } from '@/i18n';

const DEFAULT_PAGE_SIZE = 20;

/** Задержка debounce живого поиска (канон R33c: 300 мс). */
const SEARCH_DEBOUNCE_MS = 300;

/** Порядок lifecycle-значений в чипах фильтра (OQ-10: computed-статусы не фильтруются). */
const LIFECYCLE_ORDER: InvoiceLifecycleStatus[] = ['DRAFT', 'OPEN', 'VOID'];

/** Страница раздела «Счета». */
export function InvoicesListPage(): JSX.Element {
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const dealId = searchParams.get('dealId');
  const [page, setPage] = useState(0);
  const [status, setStatus] = useState<InvoiceLifecycleStatus | null>(null);
  const [searchInput, setSearchInput] = useState('');
  const [q, setQ] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [editInvoice, setEditInvoice] = useState<Invoice | null>(null);
  const [payInvoice, setPayInvoice] = useState<Invoice | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  /** q после debounce (канон SearchFieldBar/R33c) — сброс пагинации при смене строки. */
  const debouncedSetQ = useMemo(() => debounce((value: string) => {
    setQ(value);
    setPage(0);
  }, SEARCH_DEBOUNCE_MS), []);

  const queryInvoices = useQuery({
    queryKey: ['invoices', page, status ?? null, q, dealId ?? null],
    queryFn: () =>
      invoicesApi.list({
        page,
        size: DEFAULT_PAGE_SIZE,
        ...(status ? { status } : {}),
        ...(q ? { q } : {}),
        ...(dealId ? { dealId } : {}),
      }),
  });

  /** Lifecycle-переход (Выставить/Аннулировать) — PATCH status + optimistic lock (409 stale → refetch). */
  const lifecycleMutation = useMutation({
    mutationFn: ({ invoice, next }: { invoice: Invoice; next: InvoiceLifecycleStatus }) =>
      invoicesApi.update(invoice.id, { status: next, expectedUpdatedAt: invoice.updatedAt }),
    onSuccess: () => {
      setActionError(null);
      void queryClient.invalidateQueries({ queryKey: ['invoices'] });
    },
    onError: (error) => {
      setActionError(extractApiErrorMessage(error, t('invoices.edit.error')));
      void queryClient.invalidateQueries({ queryKey: ['invoices'] });
    },
  });

  /** Клик по чипу статуса: выбрать/снять (одиночный фильтр — контракт status=). */
  const handleStatusToggle = (next: InvoiceLifecycleStatus): void => {
    setPage(0);
    setStatus((prev) => (prev === next ? null : next));
  };

  /** Сброс фильтра сделки (?dealId очищается — канон PaymentsListPage). */
  const clearDealFilter = (): void => {
    setPage(0);
    setSearchParams({});
  };

  /** «Оплатить» → PaymentNewDialog с presetInvoice (W7 §3.8: «Счёт №INV-…», POST с invoiceId). */
  const payPresetInvoice: Invoice | null = payInvoice;

  return (
    <Box>
      <PageHeader
        title={t('invoices.title')}
        subtitle={t('invoices.subtitle')}
        actions={
          <Button variant="contained" startIcon={<AddIcon />} aria-label={t('invoices.create')} onClick={() => setCreateOpen(true)}>
            {t('invoices.create')}
          </Button>
        }
      />
      {actionError ? <Alert severity="error" sx={{ mb: 2 }}>{actionError}</Alert> : null}

      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
        <Typography variant="body2" color="text.secondary">
          {t('invoices.status')}:
        </Typography>
        {LIFECYCLE_ORDER.map((s) => {
          const selected = status === s;
          return (
            <Chip
              key={s}
              data-testid={`invoice-status-${s}`}
              label={formatInvoiceStatus(s, t)}
              clickable
              onClick={() => handleStatusToggle(s)}
              color={selected ? invoiceStatusColor(s) : 'default'}
              variant={selected ? 'filled' : 'outlined'}
              size="small"
            />
          );
        })}
        <TextField
          size="small"
          value={searchInput}
          placeholder={t('invoices.search')}
          inputProps={{ 'aria-label': t('invoices.search') }}
          onChange={(e) => {
            setSearchInput(e.target.value);
            debouncedSetQ(e.target.value);
          }}
          sx={{ width: 260 }}
        />
        {dealId ? (
          <Chip
            label={`${t('invoices.deal')}: ${dealId}`}
            color="primary"
            onDelete={clearDealFilter}
            size="small"
          />
        ) : null}
      </Stack>

      {queryInvoices.isLoading ? (
        <LoadingSpinner label={t('invoices.loading')} />
      ) : queryInvoices.isError ? (
        <EmptyState title={t('invoices.loadError')} description={queryInvoices.error instanceof Error ? queryInvoices.error.message : 'Неизвестная ошибка'} />
      ) : !queryInvoices.data || queryInvoices.data.content.length === 0 ? (
        <EmptyState title={t('invoices.empty')} description={t('invoices.emptyDesc')} />
      ) : (
        <Paper>
          <TableContainer>
            <Table size="medium">
              <TableHead>
                <TableRow>
                  <TableCell>№</TableCell>
                  <TableCell>{t('invoices.deal')}</TableCell>
                  <TableCell>{t('invoices.invoiceDate')}</TableCell>
                  <TableCell>{t('invoices.amount')}</TableCell>
                  <TableCell>{t('invoices.paidAmount')}</TableCell>
                  <TableCell>{t('invoices.remaining')}</TableCell>
                  <TableCell>{t('invoices.dueDate')}</TableCell>
                  <TableCell>{t('invoices.status')}</TableCell>
                  <TableCell align="right">{t('invoices.actions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(queryInvoices.data.content as Invoice[]).map((invoice) => (
                  <TableRow key={invoice.id} hover>
                    <TableCell>{formatInvoiceNumber(invoice.invoiceNumber, invoice.invoiceDate)}</TableCell>
                    <TableCell>{invoice.dealTitle ?? '—'}</TableCell>
                    <TableCell>{formatDate(invoice.invoiceDate)}</TableCell>
                    <TableCell>{formatMoney(invoice.amount, invoice.currency)}</TableCell>
                    <TableCell>{formatMoney(invoice.paidAmount, invoice.currency)}</TableCell>
                    <TableCell>{formatMoney(invoice.remaining, invoice.currency)}</TableCell>
                    <TableCell>{formatDate(invoice.dueDate)}</TableCell>
                    <TableCell>
                      <InvoiceStatusChip status={invoice.status} />
                    </TableCell>
                    <TableCell align="right">
                      {invoice.lifecycle === 'DRAFT' ? (
                        <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                          <Tooltip title={t('invoices.issue')}>
                            <IconButton
                              size="small"
                              aria-label={t('invoices.issue')}
                              disabled={lifecycleMutation.isPending}
                              onClick={() => lifecycleMutation.mutate({ invoice, next: 'OPEN' })}
                            >
                              <PlayArrowIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={t('invoices.editAction')}>
                            <IconButton size="small" aria-label={t('invoices.editAction')} onClick={() => setEditInvoice(invoice)}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={t('invoices.void')}>
                            <IconButton
                              size="small"
                              aria-label={t('invoices.void')}
                              disabled={lifecycleMutation.isPending}
                              onClick={() => lifecycleMutation.mutate({ invoice, next: 'VOID' })}
                            >
                              <CancelIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      ) : invoice.lifecycle === 'OPEN' ? (
                        <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                          <Button
                            size="small"
                            variant="outlined"
                            aria-label={t('invoices.pay')}
                            onClick={() => setPayInvoice(invoice)}
                          >
                            {t('invoices.pay')}
                          </Button>
                          <Tooltip title={t('invoices.void')}>
                            <IconButton
                              size="small"
                              aria-label={t('invoices.void')}
                              disabled={lifecycleMutation.isPending}
                              onClick={() => lifecycleMutation.mutate({ invoice, next: 'VOID' })}
                            >
                              <CancelIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      ) : (
                        '—'
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={queryInvoices.data.total}
            page={page}
            onPageChange={(_, next) => setPage(next)}
            rowsPerPage={DEFAULT_PAGE_SIZE}
            rowsPerPageOptions={[DEFAULT_PAGE_SIZE]}
            labelRowsPerPage={t('invoices.rowsPerPage')}
            labelDisplayedRows={({ from, to, count }) => `${from}–${to} ${t('invoices.of')} ${count}`}
          />
        </Paper>
      )}

      <InvoiceNewDialog open={createOpen} onClose={() => setCreateOpen(false)} presetDealId={dealId} />
      {editInvoice ? (
        <InvoiceEditDialog open onClose={() => setEditInvoice(null)} invoice={editInvoice} />
      ) : null}
      <PaymentNewDialog open={Boolean(payInvoice)} onClose={() => setPayInvoice(null)} presetInvoice={payPresetInvoice} />
    </Box>
  );
}
